import React, { useEffect, useState, useMemo } from "react";
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { HomeIcon } from "@heroicons/react/24/outline";

import { convertToSlug } from "../../utils/helps";
import { fetchPartners } from "../../features/admin/PartnerSlice";
import ApiCustom from "../../utils/ApiCustom";
import UserPDFModal from "../../components/client/UserPDFModal";

const PublicMembershipDisplay = () => {
  const { slug } = useParams();
  const { partners, loading, error } = useSelector((state) => state.partners);

  const [searchQuery, setSearchQuery] = useState(slug || "");
  const [filteredData, setFilteredData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [company, setCompany] = useState(null);

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    setSearchQuery(slug);
  }, [slug]);

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (
      partners?.filter((member) => member.approvalStatus === "Đã duyệt") &&
      partners?.filter((member) => member.approvalStatus === "Đã duyệt")
        .length > 0
    ) {
      if (!searchQuery) {
        setFilteredData(
          partners?.filter((member) => member.approvalStatus === "Đã duyệt"),
        );
      } else {
        const filtered = partners
          ?.filter((member) => member.approvalStatus === "Đã duyệt")
          .filter(
            (member) =>
              convertToSlug(member.companyName)?.includes(
                convertToSlug(searchQuery),
              ) ||
              convertToSlug(member.userFullName)?.includes(
                convertToSlug(searchQuery),
              ),
          );
        setFilteredData(filtered);
      }
    }
  }, [partners, searchQuery]);

  const toggleRowExpand = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      {
        header: "STT",
        accessorKey: "index",
        cell: ({ row }) => row.index + 1,
        meta: {
          headerClass: "w-10 md:w-16 text-center",
          cellClass: "w-10 md:w-16 text-center",
        },
      },
      columnHelper.accessor("userAvatar", {
        header: "Logo",
        cell: (info) => {
          const avatar = info.getValue();
          return avatar ? (
            <img
              src={avatar}
              alt="Company Logo"
              className="w-10 h-10 object-contain rounded-full"
              loading="lazy"
            />
          ) : (
            <div className="w-10 h-10 bg-gray-200 flex items-center justify-center text-gray-400 rounded-full text-[10px]">
              No img
            </div>
          );
        },
        meta: { headerClass: "w-16", cellClass: "w-16" },
      }),
      columnHelper.accessor("companyName", {
        header: "Tên doanh nghiệp",
        cell: ({ getValue, row }) => {
          const name = getValue();
          const fields = row?.original?.businessField || "—";
          return (
            <div className="leading-tight">
              <div className="font-medium whitespace-normal break-words md:mt-3">
                {name || "—"}
              </div>

              <div className="md:hidden whitespace-normal break-words text-gray-500">
                Lĩnh vực hoạt động
              </div>
              <div className="md:hidden text-[11px] text-gray-500 mt-0.5 whitespace-normal break-words">
                {fields}
              </div>
            </div>
          );
        },
        meta: { headerClass: "min-w-[160px]", cellClass: "align-top" },
      }),

      {
        header: "Lĩnh vực hoạt động",
        id: "businessFieldsDesktop",
        cell: ({ row }) => {
          const names = row?.original?.businessField || "—";
          return (
            <div className="whitespace-normal break-words mt-3 ">{names}</div>
          );
        },
        meta: {
          headerClass: "hidden md:table-cell w-[30%]",
          cellClass: "hidden md:table-cell align-top",
        },
      },

      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <button
            onClick={() => toggleRowExpand(row.id)}
            className="text-blue-600 hover:text-blue-700 underline text-xs"
          >
            {expandedRows[row.id] ? "Thu gọn ▲" : "Xem thêm ▼"}
          </button>
        ),
        meta: { headerClass: "w-24 text-right", cellClass: "w-24" },
      },
    ],
    [columnHelper, expandedRows],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const renderExpandedRow = (row) => {
    const rowData = row.original;
    const companyRep =
      rowData.users.filter(
        (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
      )[0] || null;
    return (
      <tr className="bg-gray-50">
        <td colSpan={columns.length} className="px-4 py-3">
          <div className="grid grid-cols-1 gap-3 p-2 text-xs">
            <h3 className="font-semibold text-gray-700">Thông tin chi tiết</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <span className="font-medium">Người đại diện:</span>{" "}
                {companyRep?.userFullName || "Chưa cập nhật"}
              </div>
              <div>
                <span className="font-medium">Chức vụ:</span>{" "}
                {companyRep?.CompaniesHasUsers?.userPosition || "Chưa cập nhật"}
              </div>
              <div>
                <span className="font-medium">Địa chỉ:</span>{" "}
                {rowData.businessAddress || "Chưa cập nhật"}
              </div>
              <div>
                <span className="font-medium">Email:</span>{" "}
                {rowData.userEmail || "Chưa cập nhật"}
              </div>
              <div>
                <span className="font-medium">Website:</span>{" "}
                {rowData.websiteOrHotline || "Chưa cập nhật"}
              </div>
            </div>
            <div className="pt-1">
              <button
                onClick={() => {
                  setCompany(rowData);
                  setIsOpenModal(true);
                }}
                className="px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 text-xs"
              >
                Thiệp chào mừng
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const handleExportExcel = async () => {
    try {
      const response = await ApiCustom("/company-export-excel", {
        responseType: "blob",
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "danh_sach_hoi_vien.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Lỗi khi tải file Excel:", error);
    }
  };

  return (
    <div className="container mx-auto overflow-x-hidden">
      <nav className="flex text-gray-500 text-sm mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2">
          <li className="inline-flex items-center">
            <Link
              to="/"
              className="inline-flex items-center hover:text-blue-600 font-medium"
            >
              <HomeIcon className="w-4 h-4 mr-2 text-gray-400" />
              Trang chủ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-3 h-3 mx-2 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M7.05 4.05a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L11.19 10 7.05 5.91a.75.75 0 010-1.06z" />
              </svg>
              <span className="ml-1 text-gray-400 font-medium">
                Danh sách hội viên
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="p-4 pt-0">
        <div className="mb-3 flex justify-end">
          <button
            className="px-2 py-1 btn btn-outline btn-success text-xs"
            onClick={handleExportExcel}
            disabled={userId == null}
          >
            Xuất Excel
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4 text-sm">Đang tải dữ liệu...</div>
        ) : error ? (
          <div className="text-center py-4 text-red-500 text-sm">
            Có lỗi xảy ra: {error}
          </div>
        ) : (
          <>
            <div className="rounded-lg shadow overflow-hidden">
              <div className="max-h-[70vh] overflow-y-auto">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-base-200">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={`px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider cursor-pointer sticky top-0 z-10 bg-base-200 ${header.column.columnDef.meta?.headerClass || ""}`}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: " 🔼",
                              desc: " 🔽",
                            }[header.column.getIsSorted()] ?? null}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody className="bg-base-100 divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <React.Fragment key={row.id}>
                          <tr className="hover:bg-gray-50">
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                className={`px-3 py-2 align-top text-gray-700 whitespace-normal break-words ${cell.column.columnDef.meta?.cellClass || ""}`}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            ))}
                          </tr>
                          {expandedRows[row.id] && renderExpandedRow(row)}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          Không tìm thấy hội viên nào phù hợp
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-3">
              <p className="text-xs text-gray-500">
                Hiển thị {filteredData.length} hội viên
              </p>
            </div>
          </>
        )}
      </div>

      <UserPDFModal
        isOpen={isOpenModal}
        company={company}
        onClose={() => setIsOpenModal(false)}
      />
    </div>
  );
};

export default PublicMembershipDisplay;